import type { DimensionDescriptor, DimensionDictionary } from "../../../../../../api/types/svTypes.ts";
import { DimensionNames } from "../../../sv/utilities/DimensionNames.ts";

export interface BalanceSheetEntityOption {
  entityNo: string;
  entityName: string;
  id: string | number | null | undefined;
}

export interface BalanceSheetEntityFilter {
  dimensionName: string;
  entityNo: string;
}

const isEntityNoDimension = (dimension: DimensionDescriptor): boolean => dimension.name === DimensionNames.EntityNo;

const toStringValue = (value: string | number | null | undefined): string =>
  value === null ? "" : String(value ?? "");

const resolveFieldByCandidates = (fields: string[], candidates: string[]): string | undefined =>
  candidates
    .map((candidate) =>
      fields.find((field) => field.localeCompare(candidate, undefined, { sensitivity: "base" }) === 0)
    )
    .find((field) => !!field);

const getEntityNoFieldName = (
  keyFieldName: string | undefined,
  availableFields: string[] | undefined,
  dictionary: DimensionDictionary[]
): string | undefined => {
  const fields = availableFields && availableFields.length > 0 ? availableFields : Object.keys(dictionary[0] ?? {});
  const dictionaryFields = Object.keys(dictionary[0] ?? {});

  // Prefer fields that are actually present in dictionary rows.
  const presentField = (field: string | undefined): string | undefined => {
    if (!field) {
      return undefined;
    }

    return dictionaryFields.find((key) => key.localeCompare(field, undefined, { sensitivity: "base" }) === 0);
  };

  const fromKeyField = presentField(keyFieldName);
  if (fromKeyField) {
    return fromKeyField;
  }

  const byConvention = resolveFieldByCandidates(dictionaryFields, [DimensionNames.EntityNo, "No.", "No"]);
  if (byConvention) {
    return byConvention;
  }

  const byRegex = dictionaryFields.find((field) => /(^no\.?$)|entity.?no/i.test(field));
  if (byRegex) {
    return byRegex;
  }

  return resolveFieldByCandidates(fields, [DimensionNames.EntityNo, "No.", "No", keyFieldName ?? ""]);
};

const getEntityNameFieldName = (
  availableFields: string[] | undefined,
  dictionary: DimensionDictionary[]
): string | undefined => {
  const fields = availableFields && availableFields.length > 0 ? availableFields : Object.keys(dictionary[0] ?? {});
  const dictionaryFields = Object.keys(dictionary[0] ?? {});

  const presentField = (field: string | undefined): string | undefined => {
    if (!field) {
      return undefined;
    }
    return dictionaryFields.find((key) => key.localeCompare(field, undefined, { sensitivity: "base" }) === 0);
  };

  const byConventionFromDictionary = resolveFieldByCandidates(dictionaryFields, ["EntityName", "Name"]);
  if (byConventionFromDictionary) {
    return byConventionFromDictionary;
  }

  const byConventionFromMetadata = fields.find(
    (field) =>
      field.localeCompare("EntityName", undefined, { sensitivity: "base" }) === 0 ||
      field.localeCompare("Name", undefined, { sensitivity: "base" }) === 0
  );
  const byConvention = presentField(byConventionFromMetadata);

  if (byConvention) {
    return byConvention;
  }

  return dictionaryFields.find((field) => field === DimensionNames.EntityNo);
};

export const getBalanceSheetEntityDimension = (dimensions: DimensionDescriptor[]): DimensionDescriptor | undefined =>
  dimensions.find(isEntityNoDimension);

export const getBalanceSheetEntityOptions = (
  dimension: DimensionDescriptor | undefined,
  fallbackDictionary: DimensionDictionary[] = []
): BalanceSheetEntityOption[] => {
  const dictionary = dimension?.dictionary?.length ? dimension.dictionary : fallbackDictionary;
  if (dictionary.length === 0) {
    return [];
  }

  const keyFieldName = getEntityNoFieldName(dimension?.keyFieldName, dimension?.availableFields, dictionary);
  if (!keyFieldName) {
    return [];
  }
  const entityNameFieldName = getEntityNameFieldName(dimension?.availableFields, dictionary);
  const optionsByKey = new Map<string, BalanceSheetEntityOption>();

  dictionary.forEach((item) => {
    const entityNo = toStringValue(item[keyFieldName]).trim();
    if (!entityNo) {
      return;
    }

    const entityName = entityNameFieldName ? toStringValue(item[entityNameFieldName]).trim() : "";
    if (!optionsByKey.has(entityNo)) {
      optionsByKey.set(entityNo, { entityNo, entityName, id: item["id"] });
    }
  });

  return [...optionsByKey.values()].sort((left, right) => left.entityNo.localeCompare(right.entityNo));
};

export const getBalanceSheetEntityFilter = (
  entity: BalanceSheetEntityOption | null
): BalanceSheetEntityFilter | undefined => {
  if (!entity) {
    return undefined;
  }

  return {
    dimensionName: DimensionNames.EntityNo,
    entityNo: entity.entityNo,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isDimensionDictionary = (value: unknown): value is DimensionDictionary =>
  isRecord(value) &&
  Object.values(value).every((item) => item === null || typeof item === "string" || typeof item === "number");

export const getBalanceSheetEntityDictionaryFromApiResponse = (data: unknown): DimensionDictionary[] => {
  const toDictionaryList = (value: unknown): DimensionDictionary[] => {
    if (Array.isArray(value)) {
      return value.filter(isDimensionDictionary);
    }
    if (isRecord(value)) {
      return Object.values(value).filter(isDimensionDictionary);
    }
    return [];
  };

  const tryExtractFromRecord = (record: Record<string, unknown>): DimensionDictionary[] => {
    const maybeEntityNoContainer = record[DimensionNames.EntityNo];
    if (maybeEntityNoContainer !== undefined) {
      const fromEntityNoContainer = toDictionaryList(maybeEntityNoContainer);
      if (fromEntityNoContainer.length > 0) {
        return fromEntityNoContainer;
      }
    }

    if (isDimensionDictionary(record) && record[DimensionNames.EntityNo] !== undefined) {
      return [record];
    }

    return [];
  };

  if (Array.isArray(data)) {
    const directRows = data.filter(isDimensionDictionary).filter((row) => row[DimensionNames.EntityNo] !== undefined);
    if (directRows.length > 0) {
      return directRows;
    }

    for (const item of data) {
      if (!isRecord(item)) {
        continue;
      }
      const extracted = tryExtractFromRecord(item);
      if (extracted.length > 0) {
        return extracted;
      }
    }
    return [];
  }

  if (isRecord(data)) {
    return tryExtractFromRecord(data);
  }

  return [];
};
